import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Code,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider
} from '@chakra-ui/react';
import { ChevronRightIcon, ChevronDownIcon, CalendarIcon, ViewIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const EditPage = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    path: '',
    description: '',
    site_id: '',
    template_id: '',
    is_home: false,
    meta_title: '',
    meta_description: '',
    status: 'draft'
  });
  const [sites, setSites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [publishingVersion, setPublishingVersion] = useState(null);
  const [deletingVersion, setDeletingVersion] = useState(null);
  const { isOpen: isVersionModalOpen, onOpen: onVersionModalOpen, onClose: onVersionModalClose } = useDisclosure();
  const { isOpen: isPublishDialogOpen, onOpen: onPublishDialogOpen, onClose: onPublishDialogClose } = useDisclosure();
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const cancelRef = React.useRef();

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (id && tenantId) {
      fetchPage();
      fetchSites();
      fetchTemplates();
    }
  }, [id, tenantId]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`/pages/${tenantId}/${id}`);
      
      if (!response || response.error) {
        throw new Error(response?.error || 'Page not found');
      }

      console.log('Fetched page data:', response);

      setFormData(response.data);
      setOriginalData(response.data);
      setVersions(response.data?.versions || []);
      setPageContent(response.data?.content);
    } catch (error) {
      console.error('Error fetching page:', error);
      toast({
        title: 'Error loading page',
        description: error.message || 'Failed to load page',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate('/pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await apiClient(`/sites/${tenantId}`);
      setSites(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiClient(`/templates/${tenantId}`);
      setTemplates(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([
        { id: 1, name: 'Default Template', template_type: 'default' },
        { id: 2, name: 'Landing Page', template_type: 'landing' },
        { id: 3, name: 'Blog Post', template_type: 'blog' },
        { id: 4, name: 'Contact Page', template_type: 'contact' },
        { id: 5, name: 'Product Page', template_type: 'product' }
      ]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Page name is required';
    }

    if (!formData.path?.trim()) {
      newErrors.path = 'URL path is required';
    } else if (!formData.path.startsWith('/')) {
      newErrors.path = 'URL path must start with /';
    }

    if (!formData.site_id) {
      newErrors.site_id = 'Please select a site';
    }

    if (formData.path && !formData.path.match(/^\/[a-z0-9\-\/]*$/)) {
      newErrors.path = 'URL path can only contain lowercase letters, numbers, hyphens, and forward slashes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const response = await apiClient(`/pages/${tenantId}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          tenant_id: tenantId
        })
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Page updated successfully',
        description: `Page "${formData.name}" has been updated`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh page data to get updated information
      await fetchPage();
    } catch (error) {
      console.error('Error updating page:', error);
      toast({
        title: 'Error updating page',
        description: error.message || 'Failed to update page',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePathChange = (e) => {
    let path = e.target.value;
    
    if (path && !path.startsWith('/')) {
      path = '/' + path;
    }
    
    setFormData({ ...formData, path });
    
    if (errors.path) {
      setErrors({ ...errors, path: null });
    }
  };

  const handleVersionSelect = async (version) => {
    setSelectedVersion(version);
    onVersionModalOpen();
  };

  // Fixed publishing functions
  const publishPage = async () => {
    try {
      setSaving(true);
      const response = await apiClient(`/pages/${tenantId}/${id}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Page published',
        description: 'The page has been published successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh page data
      await fetchPage();
    } catch (error) {
      console.error('Error publishing page:', error);
      toast({
        title: 'Error publishing page',
        description: error.message || 'Failed to publish page',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const publishVersion = async (versionId) => {
    try {
      setSaving(true);
      console.log('Publishing version:', versionId);
      const response = await apiClient(`/pages/${tenantId}/${id}/versions/${versionId}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Version published',
        description: 'The selected version has been published',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh page data
      await fetchPage();
      onPublishDialogClose();
    } catch (error) {
      console.error('Error publishing version:', error);
      toast({
        title: 'Error publishing version',
        description: error.message || 'Failed to publish version',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
      setPublishingVersion(null);
    }
  };

  const deleteVersion = async (versionId) => {
    try {
      setSaving(true);
      const response = await apiClient(`/pages/${tenantId}/${id}/versions/${versionId}`, {
        method: 'DELETE',
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Version deleted',
        description: 'The version has been deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh page data
      await fetchPage();
      onDeleteDialogClose();
    } catch (error) {
      console.error('Error deleting version:', error);
      toast({
        title: 'Error deleting version',
        description: error.message || 'Failed to delete version',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
      setDeletingVersion(null);
    }
  };

  const schedulePublication = (versionId = null) => {
    const scheduleUrl = `/scheduling/create?entity_type=page&entity_id=${id}${versionId ? `&version_id=${versionId}` : ''}`;
    navigate(scheduleUrl);
  };

  const confirmPublishVersion = (version) => {
    console.log('Confirming publish for version:', version);
    setPublishingVersion(version);
    onPublishDialogOpen();
  };

  const confirmDeleteVersion = (version) => {
    setDeletingVersion(version);
    onDeleteDialogOpen();
  };

  const createNewVersion = () => {
    navigate(`/pages/${id}/builder`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'yellow';
      case 'archived': return 'gray';
      default: return 'gray';
    }
  };

  const getVersionStatusColor = (versionType) => {
    switch (versionType) {
      case 'published': return 'green';
      case 'draft': return 'yellow';
      case 'archived': return 'gray';
      default: return 'blue';
    }
  };

  if (loading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text>Loading page...</Text>
        </VStack>
      </Center>
    );
  }

  if (!tenantId) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/pages">Pages</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit Page</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <Tabs>
        <TabList>
          <Tab>Page Settings</Tab>
          <Tab>Versions & Content</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={6} align="stretch">
                <HStack justify="space-between">
                  <Box>
                    <HStack spacing={3} mb={2}>
                      <Heading size="lg">Edit Page</Heading>
                      <Badge colorScheme={getStatusColor(formData.status)}>
                        {formData.status}
                      </Badge>
                    </HStack>
                    <Text color="gray.500">
                      Update page information and settings
                    </Text>
                  </Box>
                  <HStack>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/pages/${id}/builder`)}
                      isDisabled={saving}
                      leftIcon={<EditIcon />}
                    >
                      Open in Builder
                    </Button>
                    <Menu>
                      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} colorScheme="green" isDisabled={saving}>
                        Publish
                      </MenuButton>
                      <MenuList>
                        <MenuItem onClick={publishPage}>
                          Publish Now
                        </MenuItem>
                        <MenuItem onClick={() => schedulePublication()} icon={<CalendarIcon />}>
                          Schedule Publication
                        </MenuItem>
                      </MenuList>
                    </Menu>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/pages')}
                      isDisabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      colorScheme="blue"
                      isLoading={saving}
                      loadingText="Saving..."
                    >
                      Save Changes
                    </Button>
                  </HStack>
                </HStack>

                {/* Basic Information Card */}
                <Card bg={cardBg} borderColor={borderColor} variant="outline">
                  <CardHeader>
                    <Heading size="md">Basic Information</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4}>
                      <FormControl isRequired isInvalid={!!errors.name}>
                        <FormLabel>Page Name</FormLabel>
                        <Input
                          value={formData.name || ''}
                          onChange={(e) => {
                            setFormData({ ...formData, name: e.target.value });
                            if (errors.name) {
                              setErrors({ ...errors, name: null });
                            }
                          }}
                          placeholder="About Us"
                          isDisabled={saving}
                        />
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired isInvalid={!!errors.path}>
                        <FormLabel>URL Path</FormLabel>
                        <Input
                          value={formData.path || ''}
                          onChange={handlePathChange}
                          placeholder="/about-us"
                          isDisabled={saving}
                        />
                        <FormErrorMessage>{errors.path}</FormErrorMessage>
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          The URL where this page will be accessible
                        </Text>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Description</FormLabel>
                        <Textarea
                          value={formData.description || ''}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Brief description of the page..."
                          rows={3}
                          isDisabled={saving}
                        />
                      </FormControl>

                      <FormControl isRequired isInvalid={!!errors.site_id}>
                        <FormLabel>Site</FormLabel>
                        <Select
                          value={formData.site_id || ''}
                          onChange={(e) => {
                            setFormData({ ...formData, site_id: e.target.value });
                            if (errors.site_id) {
                              setErrors({ ...errors, site_id: null });
                            }
                          }}
                          placeholder="Select a site"
                          isDisabled={saving}
                        >
                          {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                              {site.name} ({site.domain})
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.site_id}</FormErrorMessage>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Template</FormLabel>
                        <Select
                          value={formData.template_id || ''}
                          onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                          placeholder="Select a template"
                          isDisabled={saving}
                        >
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Status</FormLabel>
                        <Select
                          value={formData.status || 'draft'}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          isDisabled={saving}
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                          <option value="archived">Archived</option>
                        </Select>
                      </FormControl>

                      <FormControl display="flex" alignItems="center">
                        <FormLabel mb="0">
                          Set as Homepage
                        </FormLabel>
                        <Switch
                          isChecked={formData.is_home || false}
                          onChange={(e) => setFormData({ ...formData, is_home: e.target.checked })}
                          isDisabled={saving}
                        />
                        <Text fontSize="sm" color="gray.500" ml={3}>
                          This will be the default page for the site
                        </Text>
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>

                {/* SEO Settings Card */}
                <Card bg={cardBg} borderColor={borderColor} variant="outline">
                  <CardHeader>
                    <Heading size="md">SEO Settings</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4}>
                      <FormControl>
                        <FormLabel>Meta Title</FormLabel>
                        <Input
                          value={formData.meta_title || ''}
                          onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                          placeholder={formData.name || "Page title for search engines"}
                          isDisabled={saving}
                        />
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          Recommended: 50-60 characters
                        </Text>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Meta Description</FormLabel>
                        <Textarea
                          value={formData.meta_description || ''}
                          onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                          placeholder="Brief description for search engines..."
                          rows={3}
                          isDisabled={saving}
                        />
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          Recommended: 150-160 characters
                        </Text>
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Page Information Card */}
                {originalData && (
                  <Card bg={cardBg} borderColor={borderColor} variant="outline">
                    <CardHeader>
                      <Heading size="md">Page Information</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={2} align="start">
                        <Text fontSize="sm">
                          <strong>Created:</strong> {new Date(originalData.created_at).toLocaleString()}
                        </Text>
                        <Text fontSize="sm">
                          <strong>Last Updated:</strong> {new Date(originalData.updated_at).toLocaleString()}
                        </Text>
                        {originalData.published_at && (
                          <Text fontSize="sm">
                            <strong>Published:</strong> {new Date(originalData.published_at).toLocaleString()}
                          </Text>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                )}
              </VStack>
            </form>
          </TabPanel>

          <TabPanel px={0}>
            <VStack spacing={6} align="stretch">
              <HStack justify="space-between">
                <Box>
                  <Heading size="lg">Page Versions & Content</Heading>
                  <Text color="gray.500">
                    Manage different versions of your page content
                  </Text>
                </Box>
                <Button
                  colorScheme="blue"
                  onClick={createNewVersion}
                  isDisabled={saving}
                  leftIcon={<EditIcon />}
                >
                  Edit in Builder
                </Button>
              </HStack>

              {/* Page Versions Table */}
              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardHeader>
                  <Heading size="md">Page Versions</Heading>
                </CardHeader>
                <CardBody>
                  {versions.length > 0 ? (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Version</Th>
                            <Th>Type</Th>
                            <Th>Created</Th>
                            <Th>Status</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {versions.map((version) => (
                            <Tr key={version.id}>
                              <Td>
                                <Text fontWeight="medium">
                                  Version {version.version_number}
                                </Text>
                              </Td>
                              <Td>
                                <Badge colorScheme={getVersionStatusColor(version.version_type)}>
                                  {version.version_type}
                                </Badge>
                              </Td>
                              <Td>
                                <Text fontSize="sm" color="gray.500">
                                  {new Date(version.created_at).toLocaleString()}
                                </Text>
                              </Td>
                              <Td>
                                {version.version_type === 'published' ? (
                                  <Badge colorScheme="green">Live</Badge>
                                ) : (
                                  <Badge colorScheme="gray">Draft</Badge>
                                )}
                              </Td>
                              <Td>
                                <HStack spacing={2}>
                                  <IconButton
                                    size="sm"
                                    variant="outline"
                                    icon={<ViewIcon />}
                                    onClick={() => handleVersionSelect(version)}
                                    aria-label="View version"
                                  />
                                  
                                  <Menu>
                                    <MenuButton 
                                      as={IconButton} 
                                      size="sm" 
                                      variant="outline"
                                      icon={<ChevronDownIcon />}
                                      aria-label="Version actions"
                                    />
                                    <MenuList>
                                      {version.version_type !== 'published' && (
                                        <>
                                          <MenuItem onClick={() => confirmPublishVersion(version)}>
                                            Publish Now
                                          </MenuItem>
                                          <MenuItem 
                                            onClick={() => schedulePublication(version.id)}
                                            icon={<CalendarIcon />}
                                          >
                                            Schedule Publication
                                          </MenuItem>
                                          <Divider />
                                        </>
                                      )}
                                      <MenuItem 
                                        onClick={() => navigate(`/page-builder/${id}?version=${version.id}`)}
                                        icon={<EditIcon />}
                                      >
                                        Edit in Builder
                                      </MenuItem>
                                      {version.version_type !== 'published' && (
                                        <MenuItem 
                                          onClick={() => confirmDeleteVersion(version)}
                                          icon={<DeleteIcon />}
                                          color="red.500"
                                        >
                                          Delete Version
                                        </MenuItem>
                                      )}
                                    </MenuList>
                                  </Menu>
                                </HStack>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Center py={8}>
                      <VStack spacing={4}>
                        <Text color="gray.500">No versions available</Text>
                        <Button
                          colorScheme="blue"
                          onClick={createNewVersion}
                          leftIcon={<EditIcon />}
                        >
                          Create First Version
                        </Button>
                      </VStack>
                    </Center>
                  )}
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Publish Confirmation Dialog */}
      <AlertDialog
        isOpen={isPublishDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onPublishDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Publish Version
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to publish version {publishingVersion?.version_number}? 
              This will make it the live version of the page and replace any currently published version.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onPublishDialogClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="green" 
                onClick={() => publishVersion(publishingVersion?.id)} 
                ml={3}
                isLoading={saving}
              >
                Publish
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Version
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete version {deletingVersion?.version_number}? 
              This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteDialogClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={() => deleteVersion(deletingVersion?.id)} 
                ml={3}
                isLoading={saving}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Version Content Modal */}
      <Modal isOpen={isVersionModalOpen} onClose={onVersionModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Version {selectedVersion?.version_number} Content
            <Badge ml={2} colorScheme={getVersionStatusColor(selectedVersion?.version_type)}>
              {selectedVersion?.version_type}
            </Badge>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedVersion && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="medium" mb={2}>Content:</Text>
                  {selectedVersion.content ? (
                    typeof selectedVersion.content === 'string' ? (
                      <Text>{selectedVersion.content}</Text>
                    ) : (
                      <Code display="block" whiteSpace="pre-wrap" p={4} bg="gray.50">
                        {JSON.stringify(selectedVersion.content, null, 2)}
                      </Code>
                    )
                  ) : (
                    <Text color="gray.500">No content</Text>
                  )}
                </Box>

                {selectedVersion.layout_data && (
                  <Box>
                    <Text fontWeight="medium" mb={2}>Layout Data:</Text>
                    <Code display="block" whiteSpace="pre-wrap" p={4} bg="gray.50">
                      {JSON.stringify(selectedVersion.layout_data, null, 2)}
                    </Code>
                  </Box>
                )}

                <Box>
                  <Text fontSize="sm" color="gray.500">
                    <strong>Created:</strong> {new Date(selectedVersion.created_at).toLocaleString()}
                  </Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onVersionModalClose}>
              Close
            </Button>
            {selectedVersion?.version_type !== 'published' && (
              <Button
                colorScheme="green"
                onClick={() => {
                  confirmPublishVersion(selectedVersion);
                  onVersionModalClose();
                }}
              >
                Publish This Version
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default EditPage;